import { apiFetch } from './client';

/** The five ways of reading the specification asks for. */
export type StreamKey = 'feed' | 'latest' | 'trending' | 'following' | 'featured';

export interface Stream {
  key: StreamKey;
  name: string;
  description: string;
  /** True when the stream means nothing without knowing who is asking. */
  needs_account: boolean;
}

export interface Topic {
  name: string;
  slug: string;
}

export interface CategoryInfo extends Topic {
  description: string;
}

export interface TagInfo extends Topic {
  uses: number;
}

/** One story as it appears in a stream: enough to decide whether to open it. */
export interface StoryCard {
  id: string;
  title: string;
  subtitle: string;
  excerpt: string;
  author: { handle: string; display_name: string };
  category: Topic | null;
  tags: Topic[];
  block_count: number;
  version: number | null;
  view_count: number;
  /** Public counts (Phase 18). There is deliberately no favourite count. */
  like_count: number;
  comment_count: number;
  first_published_at: string | null;
  featured_at: string | null;
}

export interface Page<T> {
  count: number;
  page: number;
  total_pages: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface StreamFilters {
  category?: string;
  tag?: string;
  author?: string;
  page?: number;
}

export async function listStreams(): Promise<Stream[]> {
  const body = await apiFetch<{ streams: Stream[] }>('/discover');
  return body.streams;
}

export async function readStream(
  key: StreamKey,
  filters: StreamFilters = {},
): Promise<Page<StoryCard>> {
  const query = new URLSearchParams();
  if (filters.category) query.set('category', filters.category);
  if (filters.tag) query.set('tag', filters.tag);
  if (filters.author) query.set('author', filters.author);
  if (filters.page && filters.page > 1) query.set('page', String(filters.page));

  const suffix = query.toString();
  return apiFetch<Page<StoryCard>>(`/discover/${key}${suffix ? `?${suffix}` : ''}`);
}

/** One story found by a search: a card, plus why it came back. */
export interface SearchResult extends StoryCard {
  /** The words around the match, with <mark> around them. May be empty. */
  snippet: string;
}

/**
 * Search published stories.
 *
 * The same narrowing as a stream — category, tag, author — with a query on top,
 * because that is what search is: one more way of narrowing the same base.
 */
export async function search(q: string, filters: StreamFilters = {}): Promise<Page<SearchResult>> {
  const query = new URLSearchParams({ q });
  if (filters.category) query.set('category', filters.category);
  if (filters.tag) query.set('tag', filters.tag);
  if (filters.author) query.set('author', filters.author);
  if (filters.page && filters.page > 1) query.set('page', String(filters.page));

  return apiFetch<Page<SearchResult>>(`/search?${query.toString()}`);
}

export interface SavedStories extends Page<StoryCard> {
  /** How many kept stories are no longer readable. Said, not silently dropped. */
  unavailable: number;
}

/** What you have kept and can still read, newest kept first. */
export async function listSaved(page = 1): Promise<SavedStories> {
  return apiFetch<SavedStories>(`/me/favorites${page > 1 ? `?page=${page}` : ''}`);
}

export async function listCategories(): Promise<CategoryInfo[]> {
  const body = await apiFetch<{ categories: CategoryInfo[] }>('/categories');
  return body.categories;
}

export async function listTags(): Promise<TagInfo[]> {
  const body = await apiFetch<{ tags: TagInfo[] }>('/tags');
  return body.tags;
}

/** Follow an author, or stop. The edge the Following stream reads. */
export async function followAuthor(handle: string): Promise<{ is_following: boolean }> {
  return apiFetch<{ is_following: boolean }>(`/authors/${handle}/follow`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
}

export async function unfollowAuthor(handle: string): Promise<{ is_following: boolean }> {
  return apiFetch<{ is_following: boolean }>(`/authors/${handle}/follow`, { method: 'DELETE' });
}
